import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Plus, Search, Trash2, PenLine, Eye, Gift } from "lucide-react";
import { toast } from "react-hot-toast";

import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { loyaltyLinks } from "@/components/lib/SidebarLinks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Pagination from "@/components/lib/Pagination";
import { DeleteConfirmDialog } from "@/components/dialog/common-dialog";
import TableSkeleton from "@/components/skeleton/table-skeletion";

import useAuth from "@/hooks/useAuth";
import useDebounce from "@/hooks/useDebounce";
import hasPermission from "@/hooks/hasPermission";
import {
  fetchSpendGetDiscountOffers,
  deleteSpendGetDiscountOfferById,
  toggleSpendGetDiscountOfferStatusById,
  setOfferSearch,
  setStatusFilter,
  setPerPage,
  setCurrentPage,
  clearSpendGetDiscountOfferMessages,
} from "@/redux/slice/loyalty/spendGetDiscountOfferSlice";
import ViewSpendGetDiscountOfferModal from "@/components/loyalty/offers/spendGetDiscount/ViewSpendGetDiscountOfferModal";

export default function SpendGetDiscountOffer() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const isRouteCleanup = useRef(false);

  const {
    offers,
    getLoading,
    search,
    status,
    perPage,
    currentPage,
    totalPages,
    success,
    error,
  } = useSelector((state) => state.loyalty.spendGetDiscountOffer);

  const debouncedSearch = useDebounce(search, 800);

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [viewOfferModal, setViewOfferModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  // Route cleanup reset
  useEffect(() => {
    return () => {
      isRouteCleanup.current = true;
      dispatch(setOfferSearch(""));
      dispatch(setStatusFilter(""));
      dispatch(setCurrentPage(1));
      setTimeout(() => {
        isRouteCleanup.current = false;
      }, 0);
    };
  }, [dispatch, location.pathname]);

  // Fetch offers
  useEffect(() => {
    if (!user?.restaurant_id) return;
    dispatch(
      fetchSpendGetDiscountOffers({
        restaurant_id: user.restaurant_id,
        page: currentPage,
        per_page: perPage,
        search: debouncedSearch,
        status: status,
      })
    );
  }, [dispatch, user?.restaurant_id, currentPage, perPage, debouncedSearch, status]);

  // Handlers
  const handleSearchChange = useCallback(
    (e) => {
      dispatch(setOfferSearch(e.target.value));
      dispatch(setCurrentPage(1));
    },
    [dispatch]
  );

  const handleStatusFilterChange = useCallback(
    (value) => {
      dispatch(setStatusFilter(value));
      dispatch(setCurrentPage(1));
    },
    [dispatch]
  );

  const handleToggleStatus = async (id) => {
    try {
      const res = await dispatch(toggleSpendGetDiscountOfferStatusById(id));
      if (toggleSpendGetDiscountOfferStatusById.fulfilled.match(res)) {
        toast.success("Offer status updated successfully!");
      } else {
        toast.error(res.payload || "Failed to update status");
      }
    } catch (err) {
      toast.error(err.message || "Failed to update status");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedDeleteId) return;
    try {
      dispatch(clearSpendGetDiscountOfferMessages());
      const res = await dispatch(deleteSpendGetDiscountOfferById(selectedDeleteId));
      if (deleteSpendGetDiscountOfferById.fulfilled.match(res)) {
        toast.success("Offer deleted successfully!");
        setDeleteDialog(false);
        setSelectedDeleteId(null);
      } else {
        toast.error(res.payload || "Failed to delete offer");
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete offer");
    }
  };

  if (getLoading && offers.length === 0) {
    return <TableSkeleton links={loyaltyLinks} />;
  }

  return (
    <Layout2
      pageName={{ title: "Spend & Get Discount Offers" }}
      paths={[
        { name: "Loyalty", path: "/loyalty" },
        { name: "Offers" },
        { name: "Spend & Get Discount" },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={loyaltyLinks} />}
    >
      <div className="container flex flex-col gap-6 py-4">
        {/* Header Bar */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-cwhite flex items-center gap-2">
              <Gift className="w-6 h-6 text-cyellow" />
              Spend & Get Discount Offers
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Manage minimum spend threshold and discount reward offers
            </p>
          </div>

          {hasPermission("loyalty_offer.spend_discount_offer.create_offer") && (
            <Link to="/offer/spend-get-discount/add">
              <Button className="bg-cyellow text-black hover:bg-yellow-500 font-medium">
                <Plus className="w-4 h-4 mr-2" /> Create Offer
              </Button>
            </Link>
          )}
        </div>

        {/* Filters Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-cblack4 p-4 rounded-lg border border-neutral-800">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by offer name..."
              value={search}
              onChange={handleSearchChange}
              className="pl-9 bg-cblack3 text-cwhite border-neutral-700"
            />
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <span className="text-sm text-gray-400 whitespace-nowrap">Filter Status:</span>
            <Select value={status} onValueChange={handleStatusFilterChange}>
              <SelectTrigger className="w-36 bg-cblack3 text-cwhite border-neutral-700">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="bg-cblack3 text-cwhite border-neutral-700">
                <SelectGroup>
                  <SelectItem value="0">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Offers Table */}
        <div className="bg-cblack4 rounded-lg border border-neutral-800 overflow-hidden">
          <Table>
            <TableHeader className="bg-cblack3">
              <TableRow className="border-neutral-800">
                <TableHead className="text-gray-300 font-semibold">Offer Name</TableHead>
                <TableHead className="text-gray-300 font-semibold text-center">Spend Threshold</TableHead>
                <TableHead className="text-gray-300 font-semibold text-center">Discount</TableHead>
                <TableHead className="text-gray-300 font-semibold text-center">Status</TableHead>
                <TableHead className="text-gray-300 font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {offers && offers.length > 0 ? (
                offers.map((offer) => {
                  const formattedDiscount =
                    offer.discount_type === "fixed"
                      ? `£ ${Number(offer.discount_value || 0).toFixed(2)}`
                      : `${offer.discount_value || 0}%`;

                  return (
                    <TableRow key={offer.id} className="border-neutral-800 hover:bg-cblack3/50">
                      <TableCell className="font-medium text-cwhite">
                        <div>
                          <p className="font-semibold text-cyellow">{offer.name}</p>
                          {offer.description && (
                            <p className="text-xs text-gray-400 mt-1 line-clamp-1">
                              {offer.description}
                            </p>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className="text-center font-semibold text-cwhite">
                        £{offer.spend_threshold}
                      </TableCell>

                      <TableCell className="text-center font-semibold text-cyellow">
                        {formattedDiscount}
                      </TableCell>

                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          {hasPermission("loyalty_offer.spend_discount_offer.toggle_status") ? (
                            <Switch
                              checked={offer.status === "active"}
                              onCheckedChange={() => handleToggleStatus(offer.id)}
                            />
                          ) : (
                            <Switch
                              checked={offer.status === "active"}
                              disabled
                            />
                          )}
                          <span className="text-xs capitalize font-medium text-gray-300">
                            {offer.status}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {hasPermission("loyalty_offer.spend_discount_offer.view_offer") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
                              onClick={() => {
                                setSelectedOffer(offer);
                                setViewOfferModal(true);
                              }}
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}

                          {hasPermission("loyalty_offer.spend_discount_offer.edit_offer") && (
                            <Link
                              to={`/offer/spend-get-discount/update`}
                              state={offer}
                            >
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-amber-400 hover:text-amber-300 hover:bg-amber-900/30"
                                title="Edit Offer"
                              >
                                <PenLine className="w-4 h-4" />
                              </Button>
                            </Link>
                          )}

                          {hasPermission("loyalty_offer.spend_discount_offer.delete_offer") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-cred hover:text-red-400 hover:bg-red-900/30"
                              onClick={() => {
                                setSelectedDeleteId(offer.id);
                                setDeleteDialog(true);
                              }}
                              title="Delete Offer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow className="border-neutral-800">
                  <TableCell colSpan={5} className="text-center py-10 text-gray-400">
                    No Spend & Get Discount offers found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-end mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => dispatch(setCurrentPage(page))}
            />
          </div>
        )}
      </div>

      {/* Delete Dialog */}
      <DeleteConfirmDialog
        isOpen={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Offer"
        description="Are you sure you want to delete this offer? This action cannot be undone."
      />

      {/* View Modal */}
      <ViewSpendGetDiscountOfferModal
        isOpen={viewOfferModal}
        onClose={() => {
          setViewOfferModal(false);
          setSelectedOffer(null);
        }}
        offer={selectedOffer}
      />
    </Layout2>
  );
}
